import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Divider,
    FormControlLabel,
    Switch,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import companyServices from "app/services/company-services";

// Umbrales de la pantalla de inventario.
//
// Viven en la compañía y no por usuario: cuántos días de cobertura considera
// crítico esta operación es política del negocio, no preferencia de quien mira.
// La bodega por defecto sí es personal y se recuerda en el navegador.
const FIELDS = [
    {
        key: "inventory_cover_critical_days",
        label: "Crítico bajo (días)",
        help: "Cobertura que se pinta en rojo",
    },
    {
        key: "inventory_cover_warning_days",
        label: "Alerta bajo (días)",
        help: "Cobertura que se pinta en ámbar",
    },
    {
        key: "inventory_demand_window_days",
        label: "Ventana de demanda (días)",
        help: "Historial para calcular rotación",
    },
    {
        key: "inventory_stale_days",
        label: "Sin movimiento sobre (días)",
        help: "Desde cuándo se considera frenado",
    },
];

const InventorySettingsDialog = ({ open, onClose, companyId, thresholds, managedByHaulers, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            manage_positions_in_haulers: managedByHaulers,
            inventory_cover_critical_days: thresholds?.critical ?? 7,
            inventory_cover_warning_days: thresholds?.warning ?? 21,
            inventory_demand_window_days: thresholds?.window_days ?? 90,
            inventory_stale_days: thresholds?.stale_days ?? 90,
        });
    }, [open, thresholds, managedByHaulers]);

    const save = async () => {
        try {
            setSaving(true);
            await companyServices.saveCompanySettings(companyId, form);
            enqueueSnackbar("Ajustes guardados", { variant: "success" });
            onSaved();
            onClose();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudieron guardar", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Ajustes de inventario</DialogTitle>
            <DialogContent>
                {/* Quién manda en la posición: el ERP o Haulers. Nunca los dos.
                    Con precedencia entre ambos habría que explicarle a alguien de
                    bodega por qué una posición viene de un lado u otro. */}
                <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                        <Switch
                            checked={Boolean(form.manage_positions_in_haulers)}
                            onChange={(event) => setForm({ ...form, manage_positions_in_haulers: event.target.checked })}
                        />
                    }
                    label="Administrar posiciones en Haulers"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {form.manage_positions_in_haulers
                        ? "Las posiciones se asignan acá. La ubicación que reporte el ERP queda como referencia."
                        : "Las posiciones las dicta el ERP y acá son de solo lectura. Lo que venga mal se corrige en el ERP."}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {FIELDS.map((field) => (
                        <Grid item xs={12} sm={6} key={field.key}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={field.label}
                                helperText={field.help}
                                value={form[field.key] ?? ""}
                                onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                            />
                        </Grid>
                    ))}
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        La ventana de demanda se aplica en el cálculo nocturno, así que un cambio acá se ve al día
                        siguiente. Los umbrales de color se aplican de inmediato.
                    </Typography>
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <LoadingButton variant="contained" loading={saving} onClick={save}>
                    Guardar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default InventorySettingsDialog;
